import {
  AreaChart,
  ArchiveX,
  BarChart3,
  Bot,
  Bug,
  ClipboardList,
  LayoutDashboard,
  LineChart,
  Link2,
  Mail,
  MailWarning,
  Receipt,
  ScanBarcode,
  Server,
  Settings,
  ShieldCheck,
  Sparkles,
  Store,
  UserCheck,
} from "lucide-react"

// Sidebar navigation, grouped. `label` doubles as the i18n key.
export const MENU_GROUPS = [
  {
    key: "management",
    title: "Management",
    items: [
      { path: "/dashboard", icon: LayoutDashboard, label: "Home" },
      { path: "/stores", icon: Store, label: "Stores" },
      { path: "/uninstalled-stores", icon: ArchiveX, label: "Ex-Stores" },
      { path: "/platform", icon: Server, label: "Platform" },
      { path: "/auto-bcl", icon: Bot, label: "Auto BCL" },
    ],
  },
  {
    key: "analytics",
    title: "Analytics",
    items: [
      {
        path: "/latest-blocked-orders",
        icon: ShieldCheck,
        label: "Analytics Overview",
      },
      { path: "/ai-insights", icon: Sparkles, label: "AI Insights" },
      {
        path: "/blocked-orders-analytics",
        icon: BarChart3,
        label: "Blocked Orders",
      },
      {
        path: "/blocked-orders-insights",
        icon: LineChart,
        label: "Blocked Orders Insights",
      },
      {
        path: "/orders-analytics",
        icon: ClipboardList,
        label: "Orders Analytics",
      },
      { path: "/scanner-website", icon: ScanBarcode, label: "Coupon Scanner" },
      { path: "/coupon-analyzer", icon: AreaChart, label: "Coupon Analyzer" },
      { path: "/email-history", icon: Mail, label: "Email History" },
      {
        path: "/high-risk-emails",
        icon: MailWarning,
        label: "High Risk Emails",
      },
    ],
  },
  {
    key: "configuration",
    title: "Configuration",
    items: [
      { path: "/reference-store", icon: Link2, label: "Reference Store" },
      {
        path: "/client-review-list",
        icon: UserCheck,
        label: "Client Review List",
      },
      { path: "/usage-charge", icon: Receipt, label: "Usage Charges" },
      { path: "/app-config", icon: Settings, label: "App Config" },
      { path: "/logs", icon: Bug, label: "Error Logs" },
    ],
  },
]

// Pages reached from a menu item that should keep that item highlighted
export const ROUTE_HIERARCHY = {
  "/stores": [
    "/merchant-details",
    "/plan-info",
    "/placement-edit",
    "/usage-charges-edit",
    "/common-setting-edit",
    "/campaign-charge-edit",
    "/blocked-coupon",
    "/upload-roster-csv",
    "/discount-type-edit",
    "/price-selector-edit",
    "/campaign-metafields-edit",
  ],
}

const matchesPath = (path, currentPath) =>
  currentPath === path || currentPath.startsWith(`${path}/`)

/**
 * True when `currentPath` is the menu item's page, a nested route under it,
 * or one of its child pages from ROUTE_HIERARCHY.
 */
export const isMenuItemActive = (menuPath, currentPath = "") => {
  if (matchesPath(menuPath, currentPath)) return true
  return (ROUTE_HIERARCHY[menuPath] || []).some(child =>
    matchesPath(child, currentPath),
  )
}

export const findActiveGroupKey = currentPath =>
  MENU_GROUPS.find(group =>
    group.items.some(item => isMenuItemActive(item.path, currentPath)),
  )?.key

const ALL_ITEMS = MENU_GROUPS.flatMap(group =>
  group.items.map(item => ({ ...item, group: group.title })),
)

/**
 * Pages matching `query` for the quick search, best matches first:
 * label starts with it → a word in the label starts with it → label contains it
 * → section title contains it. An empty query returns every page in menu order.
 */
export const searchMenuItems = (query = "") => {
  const q = query.trim().toLowerCase()
  if (!q) return ALL_ITEMS

  const scoreOf = item => {
    const label = item.label.toLowerCase()
    if (label.startsWith(q)) return 0
    if (label.split(/\s+/).some(word => word.startsWith(q))) return 1
    if (label.includes(q)) return 2
    if (item.group.toLowerCase().includes(q)) return 3
    return -1
  }

  return ALL_ITEMS.map((item, index) => ({ item, index, score: scoreOf(item) }))
    .filter(entry => entry.score >= 0)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map(entry => entry.item)
}
